'use client';

import React from 'react';
import { Button } from '@/components/ui/button';

type CalcState = {
  hc: number;
  pay: number;
  hpd: number;
  days: number;
  markup: number;
  extras: number;
  night: boolean;
};

type FormData = {
  name: string;
  phone: string;
  task: string;
  email: string;
  agreed: boolean;
};

const ru = new Intl.NumberFormat('ru-RU');

function fmt(n: number) {
  return ru.format(Math.round(n));
}

function useTempStaffCalc(defaults: CalcState) {
  const [state, setState] = React.useState<CalcState>(defaults);
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
    try {
      const raw = localStorage.getItem('temp_staff_calc');
      if (raw) {
        const parsed = JSON.parse(raw);
        setState({ ...defaults, ...parsed });
      }
    } catch { }
  }, []);

  React.useEffect(() => {
    if (isClient) {
      localStorage.setItem('temp_staff_calc', JSON.stringify(state));
    }
  }, [state, isClient]);

  const hoursTotal = state.hc * state.hpd * state.days;
  const hourly = state.pay * (state.night ? 1.2 : 1.0);
  const payroll = hoursTotal * hourly;
  const fee = payroll * (state.markup / 100) + state.hc * state.extras;
  const total = payroll + fee;

  return {
    state,
    setState,
    result: {
      hoursTotal,
      payroll,
      fee,
      total,
    },
  };
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white h-[72px] min-w-0">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base font-black">{icon}</div>
      <div className="min-w-0">
        <div className="text-xl font-extrabold leading-none truncate">{value}</div>
        <div className="text-sm opacity-90 leading-none truncate">{label}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm min-w-0">
      <div className="text-xs text-slate-500 truncate">{label}</div>
      <div className="text-2xl font-black text-[#003B9B] truncate">{value}</div>
    </div>
  );
}

function LabeledInput({
  label,
  type = 'text',
  value,
  placeholder,
  onChange,
}: {
  label: string;
  type?: React.HTMLInputTypeAttribute;
  value: number | string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs text-slate-500">{label}</span>
      <input
        type={type}
        value={typeof value === 'number' && !Number.isFinite(value) ? '' : value}
        placeholder={placeholder}
        onChange={onChange}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B] text-sm"
      />
    </label>
  );
}

export default function TemporaryStaffPage() {
  const { state, setState, result } = useTempStaffCalc({
    hc: 10,
    pay: 300,
    hpd: 8,
    days: 10,
    markup: 22,
    extras: 0,
    night: false,
  });

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    phone: '',
    task: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const getStatusColorClass = (status: string) => {
    if (status.includes('Ошибка') || status.includes('сети')) return 'text-red-600';
    if (status.includes('Пожалуйста')) return 'text-amber-600';
    if (status.includes('успешно')) return 'text-green-600';
    return 'text-slate-500';
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setFormStatus('Пожалуйста, заполните все обязательные поля (Имя, Телефон, Email) для отправки заявки.');
      return;
    }

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setFormStatus('Отправка...');

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/temporary-staff-requests`;

    const { agreed, ...dataToSend } = formData;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            ...dataToSend,
            formName: 'Временный персонал'
          },
        }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', task: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Неизвестная ошибка сервера (Strapi)';
        setFormStatus(`Произошла ошибка: ${errorMessage}`);
        console.error('Ошибка API:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение или убедитесь, что Strapi запущен.');
      console.error('Ошибка fetch/сети:', error);
    }
  };

  return (
    <main className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-black">Временный персонал</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95">
            Оперативно усилим вашу команду на пик нагрузки, для замещения или проекта.
            Запуск от 24 до 72 часов, электронные табели, бригадир на объекте (по запросу).
            Прозрачные ставки и отчётность.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
            >
              <a href="#calc" className="flex items-center justify-center h-full">Рассчитать бюджет</a>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white"
            >
              <a href="#when" className="flex items-center justify-center h-full">Когда это выгодно</a>
            </Button>
          </div>
          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4 overflow-x-auto">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 min-w-0">
              <KPI icon="⚡" value="24–72 ч" label="старт работ" />
              <KPI icon="🔄" value="≤ 24 ч" label="замена сотрудника" />
              <KPI icon="📄" value="e-табель" label="подпись и хранение" />
              <KPI icon="📊" value="SLA" label="еженед. отчёты" />
            </div>
          </div>
        </div>
      </section>

      {/* When Section */}
      <section id="when" className="py-20 bg-[#f6f8ff]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid gap-6 md:grid-cols-3 min-w-0">
          {[
            {
              title: 'Сезонные пики',
              text:
                'Акции, распродажи, сбор урожая, переучёт, логистические волны. Быстро масштабируемся и также быстро сокращаем команду.',
            },
            {
              title: 'Замещение и форс-мажоры',
              text:
                'Больничные, отпуска, простой оборудования — подстрахуем ключевые смены без найма в штат.',
            },
            {
              title: 'Проектные задачи',
              text:
                'Переезд склада, инвентаризация, фасовка, мерчандайзинг, промо — под ключ с бригадиром.',
            },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl bg-white p-6 shadow-md min-w-0">
              <h3 className="text-xl font-black">{c.title}</h3>
              <p className="mt-2 text-slate-700">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cooperation, What's Included, Safety Section */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid gap-6 md:grid-cols-3 min-w-0">
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">Модели сотрудничества</h3>
            <ul className="mt-3 space-y-2 text-black list-disc pl-5">
              <li><b>Почасовая оплата:</b> оплата за фактически отработанные часы.</li>
              <li><b>Фиксированная оплата:</b> фикс за смену/задачу (KPI по выработке).</li>
              <li><b>Стоимость проекта:</b> смета на проект с SLA и штрафами/бонусами.</li>
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              Документы: договор оказания услуг + акт/табель. Работаем в рамках ТК РФ и закона о персональных данных.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">Что входит</h3>
            <ul className="mt-3 text-black list-disc pl-5 space-y-2">
              <li>Поиск, оформление и инструктаж персонала</li>
              <li>Электронные табели (ЭДО) и отчётность</li>
              <li>Смена/замена в течение 24 часов</li>
              <li>Бригадир/куратор на объекте (по запросу)</li>
              <li>ЗИЗ/форма и медосмотры — при необходимости</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">Ограничения и безопасность</h3>
            <p className="mt-2 text-black">
              Не направляем на работы с опасными/вредными условиями и на позиции, требующие лицензий/допусков без их наличия у Заказчика. Соблюдаем требования охраны труда и инструктажи.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Список ограничений и ответственность сторон фиксируются в договоре и заявке.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calc" className="py-20 bg-[#f6f8ff]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid gap-6 md:grid-cols-2 min-w-0">
          {/* Calculator */}
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">Калькулятор временного персонала</h3>
            <p className="mt-1 text-xs text-slate-500">
              Ориентировочный расчёт. Реальная ставка зависит от задач, графика, региона и рисков.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 min-w-0">
              <LabeledInput label="Число сотрудников" type="number" value={state.hc} onChange={(e) => setState({ ...state, hc: Number(e.target.value) || 0 })} />
              <LabeledInput label="Ставка сотрудника, ₽/час (брутто)" type="number" value={state.pay} onChange={(e) => setState({ ...state, pay: Number(e.target.value) || 0 })} />
              <LabeledInput label="Часов в день на человека" type="number" value={state.hpd} onChange={(e) => setState({ ...state, hpd: Number(e.target.value) || 0 })} />
              <LabeledInput label="Дней работы" type="number" value={state.days} onChange={(e) => setState({ ...state, days: Number(e.target.value) || 0 })} />
              <LabeledInput label="Наценка агентства, %" type="number" value={state.markup} onChange={(e) => setState({ ...state, markup: Number(e.target.value) || 0 })} />
              <LabeledInput label="Доп. расходы на 1 чел., ₽ (форма/ЗИЗ)" type="number" value={state.extras} onChange={(e) => setState({ ...state, extras: Number(e.target.value) || 0 })} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input id="night" type="checkbox" className="h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]" checked={state.night} onChange={(e) => setState({ ...state, night: e.target.checked })} />
              <label htmlFor="night" className="text-sm text-slate-700">Ночные/праздничные (надбавка 20%)</label>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 min-w-0">
              <StatCard label="Фонд часов (всего)" value={ru.format(result.hoursTotal)} />
              <StatCard label="ФОТ (зарплата), ₽" value={`${fmt(result.payroll)} ₽`} />
              <StatCard label="Комиссия / наценка, ₽" value={`${fmt(result.fee)} ₽`} />
              <StatCard label="Итого к оплате, ₽" value={`${fmt(result.total)} ₽`} />
            </div>
            <p className="mt-2 text-xs text-slate-500">* Расчёт без учёта сверхурочных/разъездных/суточных, при необходимости — добавим.</p>
          </div>

          {/* SLA Section */}
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">SLA и отчётность</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full table-auto border-collapse text-base min-w-0">
                <thead>
                  <tr className="text-left text-black font-bold">
                    <th className="border-b border-slate-200 p-3">Метрика</th>
                    <th className="border-b border-slate-200 p-3">Цель</th>
                    <th className="border-b border-slate-200 p-3">Комментарий</th>
                  </tr>
                </thead>
                <tbody className="text-slate-800">
                  <tr><td className="border-b border-slate-200 p-3">Старт работ</td><td className="border-b border-slate-200 p-3">24–72 ч</td><td className="border-b border-slate-200 p-3">после заявки и подписания документов</td></tr>
                  <tr><td className="border-b border-slate-200 p-3">Замена</td><td className="border-b border-slate-200 p-3">≤ 24 ч</td><td className="border-b border-slate-200 p-3">по запросу Заказчика</td></tr>
                  <tr><td className="border-b border-slate-200 p-3">Табели</td><td className="border-b border-slate-200 p-3">ежедневно</td><td className="border-b border-slate-200 p-3">электронная подпись, сверка часов</td></tr>
                  <tr><td className="border-b border-slate-200 p-3">Отчёт</td><td className="border-b border-slate-200 p-3">еженедельно</td><td className="border-b border-slate-200 p-3">часы, выработка, инциденты</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Условия SLA утверждаются в заявке. Возможны KPI по выработке и качеству.</p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 min-w-0">
          <div className="rounded-2xl bg-white p-6 shadow-md min-w-0">
            <h3 className="text-xl font-black">Запросить временный персонал</h3>
            <form className="mt-4 grid gap-4 sm:grid-cols-2 min-w-0">
              <LabeledInput label="Имя" placeholder="Как к вам обращаться" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <LabeledInput label="Телефон" placeholder="+7 () ___--___--__" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              <LabeledInput label="Задача" placeholder="Например: 12 грузчиков, 2 смены, 10 дней" value={formData.task} onChange={(e) => setFormData({ ...formData, task: e.target.value })} />
              <LabeledInput label="Email" placeholder="for@example.com" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />

              <div className="sm:col-span-2 mt-2">
                <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600">
                  <input type="checkbox" checked={formData.agreed} onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })} className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]" />
                  <span>
                    Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
                  </span>
                </label>
              </div>
            </form>

            <a href="#" onClick={handleSubmit} className="mt-4 inline-block rounded-xl bg-[#FF7C22] px-5 py-3 font-bold text-white shadow transition-all duration-200 hover:brightness-[105%] hover:-translate-y-[1px]">
              Отправить
            </a>

            {formStatus && <p className={`mt-2 text-xs ${getStatusColorClass(formStatus)}`}>{formStatus}</p>}

            <p className="mt-2 text-xs text-slate-500">При необходимости предоставим шаблон договора и чек-лист запуска.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
